import * as React from 'react';
import dayjs from 'dayjs';
import { MdFileDownload } from 'react-icons/md';
import { toast } from 'react-toastify';
import { useAuth } from '../../Context/AuthContext';
import { exportClaims360List } from '../../Services/Claim360Service';
import CustomDatePicker from '../Common/CustomDatePicker';

// When a row was checked: claim status / denial rows carry checkedOn, eligibility rows createdOn (both UTC).
const checkedOn = (row) => row.checkedOn ?? row.createdOn ?? null;

// Export control of the Claims 360 lists. The first click on Export slides the checked-on start / end date pickers
// (the Analytics CustomDatePicker) open to its left; the next click downloads the rows the list shows (after search
// and filters) within those dates, as .xlsx in the emailed bulk report format. Escape closes the pickers.
export default function ExportListButton({ kind, rows, disabled = false }) {
  const { token } = useAuth();
  const [open, setOpen] = React.useState(false);
  const [fromDate, setFromDate] = React.useState('');
  const [toDate, setToDate] = React.useState('');
  const [exporting, setExporting] = React.useState(false);

  // Dates compare as the user's local calendar day the check ran, inclusive at both ends.
  const rowsInRange = React.useMemo(() => (rows || []).filter((row) => {
    if (!fromDate && !toDate) return true;
    const checked = checkedOn(row);
    if (!checked) return false;
    const day = dayjs(checked).format('YYYY-MM-DD');
    return (!fromDate || day >= fromDate) && (!toDate || day <= toDate);
  }), [rows, fromDate, toDate]);

  const invalidRange = Boolean(fromDate && toDate && fromDate > toDate);

  React.useEffect(() => {
    if (!open) return undefined;
    const handleEscape = (event) => {
      if (event.key === 'Escape' && !exporting) setOpen(false);
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [open, exporting]);

  const handleExport = async () => {
    if (!open) {
      setOpen(true);
      return;
    }
    if (invalidRange) {
      toast.error('The start date must be on or before the end date.');
      return;
    }
    const ids = rowsInRange.map((row) => row.id).filter((id) => id !== undefined && id !== null);
    if (!ids.length) {
      toast.info('There are no records to export for these dates.');
      return;
    }
    setExporting(true);
    try {
      await exportClaims360List(kind, ids, token);
      setOpen(false);
      setFromDate('');
      setToDate('');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error(error.message || 'Could not export the list.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="d-flex align-items-center gap-2">
      {/* Slides open leftwards from the button; the calendars are portalled, so the clipping doesn't cut them. */}
      <div
        className="d-flex align-items-center gap-2 overflow-hidden"
        aria-hidden={!open}
        style={{
          maxWidth: open ? 340 : 0,
          opacity: open ? 1 : 0,
          transition: 'max-width 0.3s ease, opacity 0.2s ease',
          paddingTop: 6,
          paddingBottom: 2,
        }}
      >
        {open ? (
          <>
            <div style={{ width: 160, flexShrink: 0 }}>
              <CustomDatePicker
                id={`${kind}-export-start-date`}
                value={fromDate}
                onChange={setFromDate}
                maxDate={toDate ? dayjs(toDate) : undefined}
                error={invalidRange}
              />
            </div>
            <div style={{ width: 160, flexShrink: 0 }}>
              <CustomDatePicker
                id={`${kind}-export-end-date`}
                value={toDate}
                onChange={setToDate}
                minDate={fromDate ? dayjs(fromDate) : undefined}
                error={invalidRange}
              />
            </div>
          </>
        ) : null}
      </div>
      <button
        type="button"
        onClick={handleExport}
        disabled={disabled || exporting || !rows?.length || (open && (invalidRange || !rowsInRange.length))}
        title={open
          ? `${rowsInRange.length} record${rowsInRange.length === 1 ? '' : 's'} will be exported`
          : 'Pick the checked-on dates to export'}
        className="btn hc-btn-prev btn-sm fw-semibold d-flex align-items-center justify-content-center gap-1 px-3 py-2 rounded-3 text-nowrap"
      >
        {exporting
          ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
          : <MdFileDownload size={18} />}
        <span>{exporting ? 'Exporting...' : open ? `Export (${rowsInRange.length})` : 'Export'}</span>
      </button>
    </div>
  );
}
